import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HowMuchLifeInsuranceIndianaFamiliesNeed() {
  useEffect(() => {
    const SITE_URL = 'https://hoosierlifeinsurance.com';
    document.title = 'How Much Life Insurance Indiana Families Need';

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Discover exactly how much life insurance Indiana families need in 2025. Use our DIME formula and real examples from Indianapolis families to find your number.');

    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute('content', 'how much life insurance Indiana, life insurance calculator Indiana, life insurance needs Indiana families');

    // Set canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${SITE_URL}/resources/how-much-life-insurance-indiana-families-need`;

    const setOgTag = (property: string, content: string, isName = false) => {
      const attr = isName ? 'name' : 'property';
      let el = document.querySelector(`meta[${attr}="${property}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, property); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setOgTag('og:url', `${SITE_URL}/how-much-life-insurance-indiana-families-need`);
    setOgTag('og:image', 'https://public.readdy.ai/ai/img_res/2f80b5fe7a5605749104bf3ac51d9bf4.jpg');
    setOgTag('twitter:card', 'summary_large_image', true);
    setOgTag('twitter:image', 'https://readdy.ai/api/search-image?query=Indiana%20family%20calculating%20how%20much%20life%20insurance%20they%20need%20using%20worksheet%20and%20calculator%20at%20bright%20kitchen%20table%20warm%20light%20focused%20planning%20clean%20background&width=1200&height=630&seq=og-howmuch-resource&orientation=landscape');

    // Inject Schema.org JSON-LD
    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'How Much Life Insurance Do Indiana Families Really Need?',
        description: 'Calculate the right amount of life insurance for your Indiana family. Learn the proven formula local agents use to determine coverage that actually protects your loved ones.',
        author: {
          '@type': 'Person',
          name: 'Benjamin Wright'
        },
        publisher: {
          '@type': 'Organization',
          name: 'Hoosier Life Insurance',
          url: SITE_URL
        },
        datePublished: '2025-01-08T00:00:00Z',
        dateModified: new Date().toISOString(),
        url: `${SITE_URL}/resources/how-much-life-insurance-indiana-families-need`,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/resources/how-much-life-insurance-indiana-families-need`
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: SITE_URL
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Resources',
            item: `${SITE_URL}/resources`
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: 'How Much Life Insurance Do Indiana Families Really Need?',
            item: `${SITE_URL}/resources/how-much-life-insurance-indiana-families-need`
          }
        ]
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'How Much Life Insurance Do Indiana Families Really Need?',
        description: 'Calculate the right amount of life insurance for your Indiana family. Learn the proven formula local agents use to determine coverage that actually protects your loved ones.',
        url: `${SITE_URL}/resources/how-much-life-insurance-indiana-families-need`,
        inLanguage: 'en-US',
        isPartOf: {
          '@type': 'WebSite',
          url: SITE_URL,
          name: 'Hoosier Life Insurance'
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'How much life insurance do Indiana families typically need?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Most Indiana families need 10-12 times their annual income in life insurance coverage. For example, if you earn $75,000 per year you should have $750,000-$900,000 in coverage. However, the DIME method gives a more accurate calculation: add up your Debt and final expenses, Income replacement needs, Mortgage balance, and Education costs for your children. A typical Indiana family earning $75,000/year with two children may actually need $1.3 million or more in coverage when all these factors are added together.'
            }
          },
          {
            '@type': 'Question',
            name: 'What is the DIME method for calculating life insurance coverage?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'The DIME method calculates your life insurance needs by adding four categories: D (Debt and final expenses)—all debts like mortgage, car loans, credit cards, and final expenses totaling $10,000-$15,000; I (Income replacement)—your annual income multiplied by the years your family needs support (typically 10-15 years); M (Mortgage)—your remaining mortgage balance if not already counted in Debt; and E (Education)—college costs for each child, roughly $100,000 per child for an Indiana public university. Add all four figures to get your total coverage need.'
            }
          },
          {
            '@type': 'Question',
            name: 'Does a stay-at-home parent in Indiana need life insurance?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes, absolutely. A stay-at-home parent in Indiana provides services worth $40,000-$60,000 per year, including childcare ($12,000-$18,000 per child annually), housekeeping ($8,000-$12,000), meal preparation, and transportation. If the stay-at-home parent dies, the surviving working parent must pay for all these services or quit their job. Most Indiana stay-at-home parents should have $250,000-$500,000 in life insurance coverage. A 35-year-old non-smoking stay-at-home parent can typically get $300,000 in 20-year term coverage for $20-$25/month.'
            }
          },
          {
            '@type': 'Question',
            name: 'Can Indiana families rely on employer-provided life insurance as their primary coverage?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'No. Most Indiana employers offer only 1-2 times your annual salary in group life insurance coverage—far below the 10-12 times income that most families need. Additionally, employer coverage disappears completely if you change jobs, get laid off, or retire. It is also not portable, meaning if you develop a health condition while employed, you may become uninsurable for affordable individual coverage later. Always supplement employer coverage with your own personal term life policy that you own and control regardless of your employment status.'
            }
          },
          {
            '@type': 'Question',
            name: 'When should Indiana families review their life insurance coverage amount?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Review your life insurance coverage every 2-3 years or immediately after any major life event. Events that require a coverage review include: marriage (your spouse now depends on your income), birth or adoption of a child (add $100,000-$200,000 per child for education and expenses), buying a home (increase coverage to match your mortgage balance), a significant income increase (your family now expects a higher standard of living), a job change where you lose employer coverage, or your children becoming financially independent (you may need less coverage).'
            }
          }
        ]
      }
    ];

    schemas.forEach((schema, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = `schema-resource-how-much-${index}`;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      schemas.forEach((_, index) => {
        const script = document.getElementById(`schema-resource-how-much-${index}`);
        if (script) {
          document.head.removeChild(script);
        }
      });
    };
  }, []);

  return (
    <>
      <title>How Much Life Insurance Indiana Families Need | Coverage Calculator | Hoosier Life Insurance</title>
      <article className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white py-20">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="relative max-w-4xl mx-auto px-6">
            <div className="mb-6">
              <Link to="/resources" className="inline-flex items-center text-white/70 hover:text-white transition-colors text-sm">
                <i className="ri-arrow-left-line mr-2"></i>
                Back to Resources
              </Link>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">How Much Life Insurance Do Indiana Families Need? Coverage Calculator Guide</h1>
            <p className="text-xl text-white/80 mb-8">A comprehensive guide to calculating the right coverage amount for your family's financial security</p>
            <div className="flex flex-wrap gap-4 text-sm text-white/70">
              <span><i className="ri-calendar-line mr-2"></i>Updated January 2026</span>
              <span><i className="ri-time-line mr-2"></i>8 min read</span>
              <span><i className="ri-map-pin-line mr-2"></i>Indiana Coverage Guide</span>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          {/* Introduction */}
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              Determining how much life insurance your Indiana family needs isn't about guesswork—it's about protecting the people who depend on your income. Whether you're in Indianapolis, Fort Wayne, or anywhere across the Hoosier State, the right coverage amount ensures your family can maintain their lifestyle, pay off debts, and achieve long-term goals even if you're no longer there to provide.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Most Indiana families are underinsured by 40-50%, according to industry research. This guide will help you calculate the exact coverage amount your family needs using proven methods tailored to Indiana's cost of living and financial landscape.
            </p>
          </div>

          {/* Quick Answer Box */}
          <div className="bg-[#FDF6F0] border-l-4 border-[#D97642] p-6 mb-12 rounded-r-lg">
            <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
              <i className="ri-lightbulb-line text-[#D97642] mr-3 text-2xl"></i>
              Quick Answer
            </h3>
            <p className="text-gray-800 leading-relaxed">
              <strong>Most Indiana families need 10-12x their annual income in life insurance coverage.</strong> For example, if you earn $75,000/year, you should have $750,000-$900,000 in coverage. This ensures your family can replace your income for 10+ years, pay off the mortgage, cover college costs, and maintain financial stability.
            </p>
          </div>

          {/* The DIME Method */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">The DIME Method: Calculate Your Coverage in 4 Steps</h2>
            <p className="text-gray-700 mb-8">
              The DIME method is the gold standard for calculating life insurance needs. It accounts for all your family's financial obligations and future goals.
            </p>

            <div className="space-y-6">
              {/* D - Debt */}
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1A3A52] text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                    D
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Debt & Final Expenses</h3>
                    <p className="text-gray-700 mb-4">Add up all debts your family would need to pay off:</p>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                        <span><strong>Mortgage balance:</strong> Average Indiana mortgage: $185,000</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                        <span><strong>Car loans:</strong> $15,000-$30,000</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                        <span><strong>Credit cards:</strong> $5,000-$15,000</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                        <span><strong>Student loans:</strong> Variable</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                        <span><strong>Final expenses:</strong> $10,000-$15,000 (funeral, burial, estate costs)</span>
                      </li>
                    </ul>
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">Example: $185,000 + $20,000 + $8,000 + $12,000 = <strong className="text-teal-600">$225,000</strong></p>
                    </div>
                  </div>
                </div>
              </div>

              {/* I - Income */}
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D97642] text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                    I
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Income Replacement</h3>
                    <p className="text-gray-700 mb-4">Multiply your annual income by the number of years your family needs support:</p>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                        <span><strong>Young children (0-10):</strong> 15-20 years of income</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                        <span><strong>Teenagers (11-17):</strong> 10-15 years of income</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                        <span><strong>No children:</strong> 5-10 years for spouse adjustment</span>
                      </li>
                    </ul>
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">Example: $75,000/year × 12 years = <strong className="text-teal-600">$900,000</strong></p>
                    </div>
                  </div>
                </div>
              </div>

              {/* M - Mortgage */}
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1A3A52] text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                    M
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Mortgage (if not paid off in "D")</h3>
                    <p className="text-gray-700 mb-4">If you want to keep the mortgage separate from debt payoff:</p>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                        <span>Add remaining mortgage balance OR</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                        <span>Include mortgage payments in income replacement (already covered in "I")</span>
                      </li>
                    </ul>
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">Most families include mortgage in "D" to avoid double-counting: <strong className="text-teal-600">$0</strong></p>
                    </div>
                  </div>
                </div>
              </div>

              {/* E - Education */}
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D97642] text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                    E
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Education Costs</h3>
                    <p className="text-gray-700 mb-4">Estimate college costs for each child (Indiana 2025 rates):</p>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                        <span><strong>Indiana public university (IU, Purdue):</strong> $25,000/year × 4 years = $100,000 per child</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                        <span><strong>Private college (Butler, Notre Dame):</strong> $55,000/year × 4 years = $220,000 per child</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                        <span><strong>Community college (Ivy Tech):</strong> $8,000/year × 2 years = $16,000 per child</span>
                      </li>
                    </ul>
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">Example (2 kids, public university): $100,000 × 2 = <strong className="text-teal-600">$200,000</strong></p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* DIME Total */}
            <div className="mt-8 bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white rounded-xl p-8">
              <h3 className="text-2xl font-bold mb-4">Your Total DIME Coverage Need</h3>
              <div className="space-y-3 text-lg">
                <div className="flex justify-between items-center pb-3 border-b border-teal-400">
                  <span>Debt & Final Expenses (D)</span>
                  <span className="font-bold">$225,000</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-teal-400">
                  <span>Income Replacement (I)</span>
                  <span className="font-bold">$900,000</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-teal-400">
                  <span>Mortgage (M)</span>
                  <span className="font-bold">$0</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-teal-400">
                  <span>Education (E)</span>
                  <span className="font-bold">$200,000</span>
                </div>
                <div className="flex justify-between items-center pt-4 text-2xl">
                  <span className="font-bold">TOTAL COVERAGE NEEDED</span>
                  <span className="font-bold">$1,325,000</span>
                </div>
              </div>
              <p className="mt-6 text-teal-100 text-sm">
                This example shows a typical Indiana family earning $75,000/year with 2 children needs approximately $1.3 million in coverage—about 18x annual income.
              </p>
            </div>
          </div>

          {/* Indiana-Specific Factors */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Indiana-Specific Factors to Consider</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-home-4-line text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Cost of Living Variations</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                    <span><strong>Indianapolis metro:</strong> 8% above state average</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                    <span><strong>Fort Wayne, Evansville:</strong> At state average</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                    <span><strong>Rural counties:</strong> 12% below state average</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-shield-check-line text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Indiana Legal Protections</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                    <span>Life insurance proceeds are 100% creditor-protected</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                    <span>Cash value protected from bankruptcy (IC 27-1-12-14)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                    <span>No state income tax on death benefits</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-briefcase-line text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Employer Coverage Gaps</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                    <span>Most Indiana employers offer 1-2x salary (insufficient)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                    <span>Coverage ends if you leave your job</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                    <span>Supplement with personal policy for full protection</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-parent-line text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Stay-at-Home Parent Coverage</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                    <span>Childcare in Indiana: $12,000-$18,000/year per child</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                    <span>Recommend $250,000-$500,000 for stay-at-home parents</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                    <span>Covers childcare, housekeeping, transportation</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Coverage by Life Stage */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Recommended Coverage by Life Stage</h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
                <thead>
                  <tr className="bg-[#1A3A52] text-white">
                    <th className="px-6 py-4 text-left font-bold">Life Stage</th>
                    <th className="px-6 py-4 text-left font-bold">Typical Income</th>
                    <th className="px-6 py-4 text-left font-bold">Recommended Coverage</th>
                    <th className="px-6 py-4 text-left font-bold">Monthly Cost*</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-[#F9F7F4]">
                    <td className="px-6 py-4">
                      <strong className="text-gray-900">Young Professional</strong>
                      <div className="text-sm text-gray-600">Age 25-30, single, no kids</div>
                    </td>
                    <td className="px-6 py-4 text-gray-700">$45,000</td>
                    <td className="px-6 py-4 text-gray-700">$250,000-$500,000</td>
                    <td className="px-6 py-4 text-teal-600 font-bold">$15-$25</td>
                  </tr>
                  <tr className="hover:bg-[#F9F7F4]">
                    <td className="px-6 py-4">
                      <strong className="text-gray-900">Newlyweds</strong>
                      <div className="text-sm text-gray-600">Age 28-35, married, no kids</div>
                    </td>
                    <td className="px-6 py-4 text-gray-700">$65,000</td>
                    <td className="px-6 py-4 text-gray-700">$500,000-$750,000</td>
                    <td className="px-6 py-4 text-teal-600 font-bold">$30-$45</td>
                  </tr>
                  <tr className="hover:bg-[#F9F7F4]">
                    <td className="px-6 py-4">
                      <strong className="text-gray-900">Young Family</strong>
                      <div className="text-sm text-gray-600">Age 30-40, 1-2 young kids</div>
                    </td>
                    <td className="px-6 py-4 text-gray-700">$75,000</td>
                    <td className="px-6 py-4 text-gray-700">$1,000,000-$1,500,000</td>
                    <td className="px-6 py-4 text-teal-600 font-bold">$50-$75</td>
                  </tr>
                  <tr className="hover:bg-[#F9F7F4]">
                    <td className="px-6 py-4">
                      <strong className="text-gray-900">Established Family</strong>
                      <div className="text-sm text-gray-600">Age 40-50, teenagers</div>
                    </td>
                    <td className="px-6 py-4 text-gray-700">$95,000</td>
                    <td className="px-6 py-4 text-gray-700">$1,000,000-$2,000,000</td>
                    <td className="px-6 py-4 text-teal-600 font-bold">$80-$120</td>
                  </tr>
                  <tr className="hover:bg-[#F9F7F4]">
                    <td className="px-6 py-4">
                      <strong className="text-gray-900">Empty Nesters</strong>
                      <div className="text-sm text-gray-600">Age 50-60, kids independent</div>
                    </td>
                    <td className="px-6 py-4 text-gray-700">$85,000</td>
                    <td className="px-6 py-4 text-gray-700">$250,000-$500,000</td>
                    <td className="px-6 py-4 text-teal-600 font-bold">$100-$180</td>
                  </tr>
                </tbody>
              </table>
              <p className="text-sm text-gray-600 mt-4">*Monthly cost estimates for healthy non-smokers with 20-year term life insurance in Indiana</p>
            </div>
          </div>

          {/* Common Mistakes */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">5 Common Coverage Mistakes Indiana Families Make</h2>
            <div className="space-y-4">
              <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
                  <i className="ri-close-circle-line text-red-500 mr-3"></i>
                  Mistake #1: Relying Only on Employer Coverage
                </h3>
                <p className="text-gray-700 text-sm">
                  Most Indiana employers offer 1-2x salary (insufficient for families). Plus, you lose coverage if you change jobs or get laid off. Always supplement with a personal policy.
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
                  <i className="ri-close-circle-line text-red-500 mr-3"></i>
                  Mistake #2: Using the "10x Income" Rule Without Adjustments
                </h3>
                <p className="text-gray-700 text-sm">
                  The 10x rule is a starting point, but it doesn't account for debt, college costs, or stay-at-home parent value. Use the DIME method for accuracy.
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
                  <i className="ri-close-circle-line text-red-500 mr-3"></i>
                  Mistake #3: Not Insuring the Stay-at-Home Parent
                </h3>
                <p className="text-gray-700 text-sm">
                  Replacing a stay-at-home parent's contributions (childcare, housekeeping, transportation) costs $40,000-$60,000/year in Indiana. They need coverage too.
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
                  <i className="ri-close-circle-line text-red-500 mr-3"></i>
                  Mistake #4: Forgetting to Update Coverage After Major Life Events
                </h3>
                <p className="text-gray-700 text-sm">
                  Marriage, new baby, home purchase, or income increase? Your coverage needs just changed. Review your policy every 2-3 years or after major milestones.
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
                  <i className="ri-close-circle-line text-red-500 mr-3"></i>
                  Mistake #5: Choosing Coverage Based on Affordability, Not Need
                </h3>
                <p className="text-gray-700 text-sm">
                  "I can only afford $250,000" is dangerous thinking when your family needs $1 million. Term life insurance is affordable—$1 million costs $50-$75/month for most Indiana families.
                </p>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white rounded-2xl p-8 mb-12">
            <h2 className="text-3xl font-bold mb-6">Ready to Get the Right Coverage for Your Indiana Family?</h2>
            <p className="text-xl text-white/80 mb-8">
              Now that you know how much coverage you need, let's find the most affordable policy that protects your family's future.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <Link 
                to="/instant-quote" 
                className="bg-white text-[#1A3A52] px-8 py-4 rounded-lg font-bold text-center hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                Get Your Free Quote
              </Link>
              <Link 
                to="/book-appointment" 
                className="bg-[#0F2A3F] text-white px-8 py-4 rounded-lg font-bold text-center hover:bg-[#0A1F2E] transition-colors border-2 border-white/30 whitespace-nowrap"
              >
                Schedule a Coverage Review
              </Link>
            </div>
          </div>

          {/* Related Articles */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Resources</h2>

            {/* Product Pages — two-way authority signal */}
            <div className="mb-4">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Product Pages</p>
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <Link to="/term-life" className="group bg-white border-2 border-[#D97642] rounded-xl p-5 hover:bg-[#FDF6F0] transition-colors">
                  <div className="w-10 h-10 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-3 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                    <i className="ri-calendar-check-line text-xl"></i>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-1 group-hover:text-[#D97642] transition-colors">Term Life Insurance</h3>
                  <p className="text-xs text-gray-500">Most affordable way to cover your family's full need</p>
                </Link>
                <Link to="/whole-life" className="group bg-white border-2 border-gray-200 rounded-xl p-5 hover:border-[#D97642] hover:bg-[#FDF6F0] transition-colors">
                  <div className="w-10 h-10 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-3 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                    <i className="ri-infinity-line text-xl"></i>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-1 group-hover:text-[#D97642] transition-colors">Whole Life Insurance</h3>
                  <p className="text-xs text-gray-500">Permanent coverage with guaranteed cash value</p>
                </Link>
                <Link to="/mortgage-protection" className="group bg-white border-2 border-gray-200 rounded-xl p-5 hover:border-[#D97642] hover:bg-[#FDF6F0] transition-colors">
                  <div className="w-10 h-10 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-3 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                    <i className="ri-home-heart-line text-xl"></i>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-1 group-hover:text-[#D97642] transition-colors">Mortgage Protection Insurance</h3>
                  <p className="text-xs text-gray-500">Cover your mortgage specifically — from $25/month</p>
                </Link>
              </div>
            </div>

            {/* Articles */}
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Articles &amp; Guides</p>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/resources/term-vs-whole-life-indianapolis-homeowners" className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors">
                <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                  <i className="ri-scales-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#D97642] transition-colors">Term vs. Whole Life for Homeowners</h3>
                <p className="text-sm text-gray-600">Which policy type is right for your mortgage and family?</p>
              </Link>

              <Link to="/blog/life-insurance-rates-indianapolis" className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors">
                <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                  <i className="ri-price-tag-3-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#D97642] transition-colors">Life Insurance Rates in Indianapolis</h3>
                <p className="text-sm text-gray-600">Current rate tables by age, health class, and coverage amount</p>
              </Link>

              <Link to="/resources/life-insurance-mistakes-indiana-families" className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors">
                <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                  <i className="ri-error-warning-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#D97642] transition-colors">7 Mistakes Indiana Families Make</h3>
                <p className="text-sm text-gray-600">Avoid costly coverage errors that leave families underprotected</p>
              </Link>
            </div>
          </div>
        </section>

        {/* Footer CTA */}
        <section className="bg-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Questions About Your Coverage Needs?</h2>
            <p className="text-xl text-gray-700 mb-8">
              Our Indiana-based agents help families calculate the right coverage amount every day. Let's build a plan that protects your family's future.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:3176482983" className="bg-[#1A3A52] text-white px-8 py-4 rounded-lg font-bold hover:bg-[#0F2A3F] transition-colors whitespace-nowrap inline-flex items-center justify-center">
                <i className="ri-phone-line mr-2"></i>
                Call (317) 648-2983
              </a>
              <Link to="/contact" className="bg-white text-[#1A3A52] px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-colors border-2 border-[#1A3A52] whitespace-nowrap inline-flex items-center justify-center">
                <i className="ri-message-3-line mr-2"></i>
                Send a Message
              </Link>
            </div>
          </div>
        </section>
      </article>
    </>
  );
}